import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface CertificationCardProps {
  title: string;
  issuer: string;
  certNumber: string;
  validUntil: string;
  scope: string;
  image: string;
  alt: string;
  status: 'active' | 'expiring' | 'renewal';
}

export default function CertificationCard({
  title,
  issuer,
  certNumber,
  validUntil,
  scope,
  image,
  alt,
  status
}: CertificationCardProps) {
  const statusConfig = {
    active: { color: 'bg-success text-success-foreground', label: 'Active' },
    expiring: { color: 'bg-warning text-warning-foreground', label: 'Expiring Soon' },
    renewal: { color: 'bg-muted text-muted-foreground', label: 'In Renewal' }
  };

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden hover:shadow-lg transition-all duration-base">
      <div className="relative h-48 bg-muted overflow-hidden">
        <AppImage
          src={image}
          alt={alt}
          className="w-full h-full object-cover"
        />
        <div className={`absolute top-4 right-4 px-3 py-1 rounded-md text-xs font-cta font-bold ${statusConfig[status].color}`}>
          {statusConfig[status].label}
        </div>
      </div>
      
      <div className="p-6">
        <h3 className="text-xl font-headline font-bold text-foreground mb-2">{title}</h3>
        <p className="text-sm font-body text-muted-foreground mb-4">{issuer}</p>
        
        <div className="space-y-3 mb-4">
          <div className="flex items-start space-x-3">
            <Icon name="DocumentTextIcon" size={20} variant="outline" className="text-primary mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-body text-muted-foreground">Certificate Number</p>
              <p className="text-sm font-body font-semibold text-foreground">{certNumber}</p>
            </div>
          </div>
          
          <div className="flex items-start space-x-3">
            <Icon name="CalendarIcon" size={20} variant="outline" className="text-primary mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-body text-muted-foreground">Valid Until</p>
              <p className="text-sm font-body font-semibold text-foreground">{validUntil}</p>
            </div>
          </div>
          
          <div className="flex items-start space-x-3">
            <Icon name="ClipboardDocumentCheckIcon" size={20} variant="outline" className="text-primary mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-body text-muted-foreground">Scope</p>
              <p className="text-sm font-body text-foreground">{scope}</p>
            </div>
          </div>
        </div>
        
        <button className="w-full px-4 py-2.5 bg-primary text-primary-foreground rounded-md text-sm font-cta font-bold hover:opacity-90 transition-all duration-base flex items-center justify-center space-x-2">
          <Icon name="DocumentArrowDownIcon" size={18} variant="outline" />
          <span>Download Certificate</span>
        </button>
      </div>
    </div>
  );
}